import styled from 'styled-components';
import {
  Button,
  Dialog as MUIDialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import HelpIcon from '@material-ui/icons/Help';

import { normalizeUrlSlug } from '@/utils/seo';

const StyledHelpIcon = styled(HelpIcon)`
  color: ${({ theme }) => theme.colors.blacks[4]};
`;

const getActions = (
  primaryAction,
  primaryActionColor,
  onClose,
  secondaryAction,
  cancelText,
  singleAction = false
) => {
  if (!primaryAction && !secondaryAction) {
    return null;
  }
  return (
    <DialogActions className="dialog-actions">
      {!singleAction && secondaryAction && (
        <div className="dialog-secondary-action">
          <Button
            data-testid="dialog-secondary-action"
            onClick={secondaryAction.onClick}
            variant="text"
          >
            {secondaryAction.text}
          </Button>
        </div>
      )}
      {!singleAction && (
        <Button data-testid="dialog-cancel-button" onClick={onClose}>
          {cancelText || 'cancel'}
        </Button>
      )}
      {primaryAction && (
        <Button
          data-testid="dialog-primary-button"
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          color={primaryActionColor}
        >
          {primaryAction.text}
        </Button>
      )}
    </DialogActions>
  );
};

const Dialog = ({
  allowClose = true,
  ariaDescribedById,
  cancelText,
  children,
  className,
  content = [],
  fullScreen,
  fullWidth,
  helpLink,
  maxWidth,
  onClose,
  open,
  primaryAction,
  primaryActionColor = 'primary',
  secondaryAction,
  singleAction = false,
  testid,
  title,
}) => {
  const ariaId = `${normalizeUrlSlug(title || 'dialog')}-title`;
  return (
    <MUIDialog
      aria-labelledby={ariaId}
      aria-describedby={ariaDescribedById}
      className={className}
      open={open}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onClose={onClose}
      classes={{ paper: 'dialog-paper' }}
      scroll="paper"
      data-testid={testid}
    >
      <DialogTitle className="dialog-title" id={ariaId}>
        {title}
        {helpLink && (
          <a
            className="dialog-help-icon"
            key="help"
            aria-label="Help"
            color="inherit"
            href={helpLink}
            target="_blank"
            rel="noreferrer"
          >
            <StyledHelpIcon />
          </a>
        )}
        {allowClose ? (
          <IconButton
            className="dialog-close-icon"
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>

      <DialogContent className="dialog-content">
        {content.map((item, i) => {
          const { text, title: subtitle } = item;

          if (!text) {
            return item;
          }

          return (
            <DialogContentText
              key={i}
              className={`dialog-content-item ${
                i > 0 && 'dialog-content-multi'
              }`}
            >
              {subtitle && <div className="dialog-subtitle">{subtitle}</div>}
              {text}
            </DialogContentText>
          );
        })}
        {children}
      </DialogContent>
      {getActions(
        primaryAction,
        primaryActionColor,
        onClose,
        secondaryAction,
        cancelText,
        singleAction
      )}
    </MUIDialog>
  );
};

const StyledDialog = styled(Dialog)`
  && {
    .dialog-paper {
      ${(p) =>
        p.primaryAction || p.secondaryAction ? 'min-height: 220px;' : ''}
      width: 100%;
    }

    .dialog-subtitle {
      text-transform: uppercase;
      margin: 0;
      padding: 0;
      margin-bottom: 10px;
    }

    .dialog-title {
      background-color: ${({ theme, colorKey }) =>
        theme.colors.dialog.title[colorKey] || ''};
      border-bottom: 1px solid ${({ theme }) => theme.colors.neutrals[1]};
      color: ${({ text }) => text || ''};
      padding-left: 20px;
      padding-right: 60px;
      text-transform: uppercase;

      h6 {
        font-size: 16px;
        font-weight: bold;
      }

      .dialog-close-icon {
        opacity: 0.5;
        position: absolute;
        right: 5px;
        top: 5px;
      }

      .dialog-help-icon {
        text-decoration: none;
        background: ${({ theme }) => theme.colors.blacks[0]};
        color: ${({ theme }) => theme.colors.blacks[8]};
        position: absolute;
        ${(p) => (!p.allowClose ? 'right: 26px;' : 'right: 50px;')}
        top: 22px;
      }
    }

    .dialog-content {
      padding: 0;
    }

    .dialog-content-item {
      margin: 0;
      padding: 20px;
    }

    .dialog-content-multi {
      border-top: 1px solid ${({ theme }) => theme.colors.neutrals[1]};
      padding-top: 20px;
    }

    .dialog-actions {
      border-top: 1px solid ${({ theme }) => theme.colors.neutrals[1]};
      padding-top: 15px;
      padding-bottom: 15px;
      padding-right: 26px;
    }

    .dialog-secondary-action {
      position: absolute;
      bottom: 15px;
      left: 20px;
    }
  }
`;

export default StyledDialog;
